/* eslint-disable @typescript-eslint/no-explicit-any */
// Notificaciones: copia de cada correo que manda Innobo Hub (iNotifications). Solo admin (rol 19).
// Primera pantalla con el kit nuevo (components/ihub). La lista viene sin HTML; al abrir una fila se pide
// el correo completo y se muestra en un iframe aislado (sandbox sin scripts), tal como le llegó al cliente.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import PageTitle from '../../components/Common/MetaTagsHelper/metaTagsHelper';
import { getNameClient } from '../../helpers/nameClient';
import { getNotifications, getNotificationFilters, getNotificationHtml } from '../../helpers/notifications_helper';
import {
  Page,
  Card,
  Field,
  TextInput,
  SelectField,
  DateRange,
  Segmented,
  Stat,
  Badge,
  Button,
  DataTable,
  Drawer,
  MetaGrid,
  Alert,
  EmptyState,
} from '../../components/ihub';
import { ExportMenu, traerTodo } from '../../components/ihub/export';

const esAdmin = () => {
  try {
    return String(JSON.parse(localStorage.getItem('authUser') || '{}').ROL_ID) === '19';
  } catch (e) {
    return false;
  }
};

const F = 'YYYY-MM-DD';
const VACIO = () => ({
  from: moment().subtract(6, 'days').format(F),
  to: moment().format(F),
  clientId: '',
  action: '',
  status: '',
  fallback: '',
  search: '',
});
const VIAS: any = { gmail: 'Gmail', microsoft: 'Microsoft 365', smtp: 'SMTP' };
const SIGLAS = new Set(['ETA', 'ETD', 'ATA', 'ATD', 'BL', 'PO', 'SO']);

// ETA_CHANGED -> "ETA changed"
const bonito = (a: string) =>
  !a
    ? ''
    : a
        .split('_')
        .map((w, i) => {
          const u = w.toUpperCase();
          if (SIGLAS.has(u)) return u;
          const l = w.toLowerCase();
          return i === 0 ? l.charAt(0).toUpperCase() + l.slice(1) : l;
        })
        .join(' ');
const destinatarios = (s: any) =>
  String(s || '')
    .split(/[,;]\s*/)
    .map((x) => x.trim())
    .filter(Boolean);
const corta = (s: any) => (s ? moment(s).format('MMM D, HH:mm') : '');
const larga = (s: any) => (s ? moment(s).format('ddd, MMM D YYYY · HH:mm:ss') : '');
const hace = (s: any) => (s ? moment(s).fromNow() : '');
const cliente = (r: any) => r.clientName || `Client ${r.clientId}`;
const cuando = (r: any) => r.sentAt || r.createdAt;
const via = (r: any) => (r.via ? `via ${VIAS[r.via] || r.via}` : '');

export const Notifications = () => {
  const admin = esAdmin();
  const [filters, setFilters] = useState<any>(VACIO);
  const [busqueda, setBusqueda] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState<any>({ key: 'id', dir: 'DESC' });
  const [data, setData] = useState<{ listData: any[]; CountItems: number }>({ listData: [], CountItems: 0 });
  const [stats, setStats] = useState<any>(null);
  const [opciones, setOpciones] = useState<{ clients: any[]; actions: string[] }>({ clients: [], actions: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fila, setFila] = useState<any>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const abierta = useRef<any>(null);

  const order = `{'${sort.key}' : '${sort.dir}'}`;
  const poner = (patch: any) => {
    setFilters((f: any) => ({ ...f, ...patch }));
    setPage(1);
  };
  const limpiar = () => {
    setBusqueda('');
    setFilters(VACIO());
    setPage(1);
  };
  const sucio = busqueda !== '' || JSON.stringify({ ...filters, search: '' }) !== JSON.stringify(VACIO());

  // Opciones de Client y Type: se piden otra vez con Refresh y cuando cambia el total,
  // para que no se queden vacías si la tabla se llenó después de abrir la pantalla.
  useEffect(() => {
    if (!admin) return;
    getNotificationFilters()
      .then(setOpciones)
      .catch(() => null);
  }, [admin, recarga, data.CountItems]);

  // Búsqueda mientras escribes, con una pausa corta para no disparar una consulta por tecla.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f: any) => {
        if (f.search === busqueda) return f;
        setPage(1);
        return { ...f, search: busqueda };
      });
    }, 350);
    return () => clearTimeout(t);
  }, [busqueda]);

  // Lista
  useEffect(() => {
    if (!admin) return undefined;
    let vivo = true;
    setLoading(true);
    setError('');
    getNotifications({ pagination: { sizePerPage: pageSize, page }, filters, order })
      .then((d: any) => vivo && setData(d))
      .catch((e: any) => vivo && setError(e?.message || 'Error'))
      .finally(() => vivo && setLoading(false));
    return () => {
      vivo = false;
    };
  }, [admin, filters, page, pageSize, order, recarga]);

  // Métricas del rango (sin estado ni fallback, para que las tarjetas sirvan de filtro rápido)
  const baseKey = JSON.stringify([filters.from, filters.to, filters.clientId, filters.action, filters.search]);
  useEffect(() => {
    if (!admin) return undefined;
    let vivo = true;
    const [from, to, clientId, action, search] = JSON.parse(baseKey);
    const base = { from, to, clientId, action, search, status: '', fallback: '' };
    const contar = (extra: any) =>
      getNotifications({ pagination: { sizePerPage: 1, page: 1 }, filters: { ...base, ...extra }, order: "{'id' : 'DESC'}" }).then(
        (d: any) => d.CountItems || 0,
      );
    Promise.all([contar({}), contar({ status: 'SENT' }), contar({ status: 'ERROR' }), contar({ fallback: 'yes' })])
      .then(([total, sent, errors, fallback]) => vivo && setStats({ total, sent, errors, fallback }))
      .catch(() => vivo && setStats({}));
    return () => {
      vivo = false;
    };
  }, [admin, baseKey, recarga]);

  const abrir = async (row: any) => {
    abierta.current = row.id;
    setFila(row);
    setHtml(null);
    let h: string | null = '';
    try {
      h = (await getNotificationHtml(row.id)) || '';
    } catch (e) {
      h = '';
    }
    if (abierta.current === row.id) setHtml(h);
  };
  const cerrar = useCallback(() => {
    abierta.current = null;
    setFila(null);
    setHtml(null);
  }, []);

  const columns = [
    {
      key: 'createdAt',
      label: 'Sent',
      sortable: true,
      width: 150,
      render: (r: any) => (
        <>
          <span className="ihub-cell-main">{corta(cuando(r))}</span>
          <span className="ihub-cell-sub">{hace(cuando(r))}</span>
        </>
      ),
    },
    {
      key: 'clientName',
      label: 'Client',
      width: 210,
      render: (r: any) => (
        <>
          <span className="ihub-cell-main ihub-truncate" style={{ maxWidth: 210 }}>
            {cliente(r)}
          </span>
          <span className="ihub-cell-sub">#{r.clientId}</span>
        </>
      ),
    },
    {
      key: 'action',
      label: 'Type',
      sortable: true,
      width: 170,
      render: (r: any) => <Badge tone="info">{bonito(r.action)}</Badge>,
    },
    {
      key: 'subject',
      label: 'Subject',
      sortable: true,
      render: (r: any) => {
        const to = destinatarios(r.toAddresses);
        return (
          <>
            <span className="ihub-cell-main ihub-truncate">{r.subject || '(no subject)'}</span>
            <span className="ihub-cell-sub ihub-truncate">
              To {to[0] || '-'}
              {to.length > 1 ? <span className="ihub-more">+{to.length - 1}</span> : null}
            </span>
          </>
        );
      },
    },
    {
      key: 'fromAddress',
      label: 'From',
      sortable: true,
      width: 250,
      render: (r: any) => (
        <>
          <span className="ihub-cell-main ihub-truncate" style={{ maxWidth: 250 }}>
            {r.fromAddress || '-'}
          </span>
          <span className="ihub-cell-sub">{via(r)}</span>
        </>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 160,
      render: (r: any) => (
        <div className="ihub-stack">
          <Badge tone={r.status === 'SENT' ? 'success' : 'danger'} dot>
            {r.status === 'SENT' ? 'Sent' : 'Error'}
          </Badge>
          {r.fallback ? <Badge tone="warning">Fallback</Badge> : null}
        </div>
      ),
    },
  ];

  // Exportar: todo lo que cumple los filtros (sin el HTML del correo, que se ve en el detalle).
  const exportCols = [
    { label: 'Sent', width: 20, value: (r: any) => (cuando(r) ? moment(cuando(r)).format('YYYY-MM-DD HH:mm:ss') : '') },
    { label: 'Client', width: 22, value: (r: any) => `${cliente(r)} #${r.clientId}` },
    { label: 'Type', width: 18, value: (r: any) => bonito(r.action) },
    { label: 'Subject', width: 50, value: (r: any) => r.subject },
    { label: 'To', width: 34, value: (r: any) => destinatarios(r.toAddresses).join(', ') },
    { label: 'From', width: 32, value: (r: any) => r.fromAddress },
    { label: 'Via', width: 12, value: (r: any) => (r.via ? VIAS[r.via] || r.via : '') },
    { label: 'Status', width: 10, value: (r: any) => (r.status === 'SENT' ? 'Sent' : 'Error') },
    { label: 'Fallback', width: 10, value: (r: any) => (r.fallback ? 'Yes' : 'No') },
    { label: 'Error', width: 30, pdf: false, value: (r: any) => r.error },
    { label: 'Shipment GUID', width: 38, pdf: false, value: (r: any) => r.number },
  ];
  const exportarTodo = () =>
    traerTodo((p: number, size: number) => getNotifications({ pagination: { sizePerPage: size, page: p }, filters, order }));

  if (!admin) {
    return (
      <Page eyebrow="Control Panel" title="Notifications">
        <Card>
          <EmptyState icon="bx-lock-alt" title="Admins only" text="You don't have access to this page." />
        </Card>
      </Page>
    );
  }

  const cargandoStats = stats === null;
  const s = stats || {};

  return (
    <>
      <Page
        eyebrow="Control Panel"
        title="Notifications"
        subtitle="Every email Innobo Hub sends, exactly as the client got it: who sent it, through which mailbox and whether it fell back to Innobo."
        actions={
          <>
            <ExportMenu fileName={`notifications_${filters.from}_${filters.to}`} title="Notifications" columns={exportCols} fetchAll={exportarTodo} />
            <Button variant="secondary" icon="bx-refresh" onClick={() => setRecarga((n) => n + 1)}>
              Refresh
            </Button>
          </>
        }
      >
        <PageTitle>
          <title>Notifications | {getNameClient()}</title>
        </PageTitle>

        <div className="ihub-stats">
          <Stat
            label="Total"
            icon="bx-envelope"
            tone="info"
            value={s.total}
            loading={cargandoStats}
            active={!filters.status && !filters.fallback}
            onClick={() => poner({ status: '', fallback: '' })}
          />
          <Stat
            label="Sent"
            icon="bx-check-circle"
            tone="success"
            value={s.sent}
            loading={cargandoStats}
            active={filters.status === 'SENT' && !filters.fallback}
            onClick={() => poner({ status: 'SENT', fallback: '' })}
          />
          <Stat
            label="Errors"
            icon="bx-error-circle"
            tone="danger"
            value={s.errors}
            loading={cargandoStats}
            active={filters.status === 'ERROR' && !filters.fallback}
            onClick={() => poner({ status: 'ERROR', fallback: '' })}
          />
          <Stat
            label="Innobo fallback"
            icon="bx-transfer-alt"
            tone="warning"
            value={s.fallback}
            loading={cargandoStats}
            active={filters.fallback === 'yes' && !filters.status}
            onClick={() => poner({ status: '', fallback: 'yes' })}
          />
        </div>

        <Card>
          <div className="ihub-filters">
            <Field label="Search" className="ihub-filters__search">
              <TextInput
                icon="bx-search"
                placeholder="Subject, recipient, sender or shipment GUID"
                value={busqueda}
                onChange={(e: any) => setBusqueda(e.target.value)}
              />
            </Field>
            <Field label="Date range">
              <DateRange from={filters.from} to={filters.to} onChange={(from: string, to: string) => poner({ from, to })} />
            </Field>
            <Field label="Client">
              <SelectField
                placeholder="All clients"
                options={opciones.clients.map((c: any) => ({ value: String(c.id), label: `${c.name} · #${c.id}` }))}
                value={filters.clientId}
                onChange={(v: any) => poner({ clientId: v || '' })}
              />
            </Field>
            <Field label="Type">
              <SelectField
                placeholder="All types"
                options={opciones.actions.map((a: string) => ({ value: a, label: bonito(a) }))}
                value={filters.action}
                onChange={(v: any) => poner({ action: v || '' })}
              />
            </Field>
            <Field label="Status">
              <Segmented
                options={[
                  { value: '', label: 'All' },
                  { value: 'SENT', label: 'Sent' },
                  { value: 'ERROR', label: 'Error' },
                ]}
                value={filters.status}
                onChange={(v: any) => poner({ status: v })}
              />
            </Field>
            <Field label="Sent from">
              <Segmented
                options={[
                  { value: '', label: 'All' },
                  { value: 'no', label: 'Client' },
                  { value: 'yes', label: 'Innobo' },
                ]}
                value={filters.fallback}
                onChange={(v: any) => poner({ fallback: v })}
              />
            </Field>
          </div>
          {sucio ? (
            <div className="ihub-filters__foot">
              <Button variant="ghost" size="sm" icon="bx-reset" onClick={limpiar}>
                Clear filters
              </Button>
            </div>
          ) : null}
        </Card>

        {error ? (
          <Alert tone="danger" title="Couldn't load notifications">
            {error}
          </Alert>
        ) : null}

        <Card flush>
          <DataTable
            columns={columns}
            rows={data.listData}
            total={data.CountItems}
            page={page}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={(n: number) => {
              setPageSize(n);
              setPage(1);
            }}
            sort={sort}
            onSort={(nuevo: any) => {
              setSort(nuevo);
              setPage(1);
            }}
            onRowClick={abrir}
            loading={loading}
            empty={
              <EmptyState
                icon="bx-envelope-open"
                title="No emails in this range"
                text={sucio ? 'Try a wider date range or clear the filters.' : 'New emails show up here as soon as the Hub sends them.'}
              />
            }
          />
        </Card>
      </Page>

      <Drawer
        open={!!fila}
        onClose={cerrar}
        eyebrow={fila ? `${bonito(fila.action)} · #${fila.id}` : ''}
        title={fila ? fila.subject || '(no subject)' : ''}
        badges={
          fila ? (
            <>
              <Badge tone={fila.status === 'SENT' ? 'success' : 'danger'} dot>
                {fila.status === 'SENT' ? 'Sent' : 'Error'}
              </Badge>
              {fila.fallback ? <Badge tone="warning">Innobo fallback</Badge> : null}
              {fila.via ? <Badge>{VIAS[fila.via] || fila.via}</Badge> : null}
            </>
          ) : null
        }
      >
        {fila ? (
          <>
            <MetaGrid
              items={[
                {
                  label: 'Client',
                  value: (
                    <>
                      {cliente(fila)} <span className="ihub-muted">#{fila.clientId}</span>
                    </>
                  ),
                },
                {
                  label: 'Sent',
                  value: (
                    <>
                      {larga(cuando(fila))}
                      <span className="ihub-cell-sub">{hace(cuando(fila))}</span>
                    </>
                  ),
                },
                {
                  label: 'From',
                  value: (
                    <>
                      {fila.fromAddress || '-'}
                      <span className="ihub-cell-sub">
                        {via(fila)}
                        {fila.fallback ? ' · Innobo fallback' : ''}
                      </span>
                    </>
                  ),
                },
                {
                  label: 'To',
                  wide: true,
                  value: (
                    <div className="ihub-stack">
                      {destinatarios(fila.toAddresses).map((d) => (
                        <span key={d} className="ihub-chip ihub-chip--static">
                          {d}
                        </span>
                      ))}
                    </div>
                  ),
                },
                {
                  label: 'Shipment GUID',
                  value: fila.number ? <code className="ihub-code">{fila.number}</code> : '-',
                },
              ]}
            />
            {fila.error ? (
              <Alert tone="danger" title="Delivery error">
                {fila.error}
              </Alert>
            ) : null}
            <Card flush>
              <div className="ihub-email__bar">
                <span>
                  <i className="bx bx-envelope" />
                  Email as delivered
                </span>
              </div>
              {html === null ? (
                <div className="ihub-email__loading">
                  <div className="ihub-skel" style={{ width: '60%' }} />
                  <div className="ihub-skel" style={{ width: '90%' }} />
                  <div className="ihub-skel" style={{ width: '75%' }} />
                  <div className="ihub-skel" style={{ width: '82%' }} />
                </div>
              ) : html ? (
                <iframe title="email" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={html} className="ihub-email__frame" />
              ) : (
                <EmptyState icon="bx-envelope-open" title="No saved content" text="This notification was logged without its HTML." />
              )}
            </Card>
          </>
        ) : null}
      </Drawer>
    </>
  );
};

export default Notifications;
